import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, FilterIcon, DownloadIcon, SlidersIcon, PlusIcon, ChevronDownIcon, CheckIcon, XIcon, EyeIcon, PencilIcon, TrashIcon, ArrowUpDownIcon } from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
interface Service {
  id: string;
  serviceCode: string;
  serviceName: string;
  category: string;
  agency: string;
  status: 'active' | 'inactive';
  jurisdictionLevel: string;
  lastUpdated: string;
  serviceFee: number;
  description: string;
}
// Mock data
const services: Service[] = [{
  id: '1',
  serviceCode: 'BL001',
  serviceName: 'Business License Application',
  category: 'Business & Licensing',
  agency: 'Commerce Department',
  status: 'active',
  jurisdictionLevel: 'Municipal',
  lastUpdated: '2024-02-15',
  serviceFee: 150.0,
  description: 'Application process for new business licenses'
}
// Add more mock services...
];
export const ServiceConfigurationPage = () => {
  const {
    navigate
  } = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string>('category');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Filter options (would come from API)
  const categories = ['Business & Licensing', 'Property Services', 'Public Safety'];
  const agencies = ['Commerce Department', 'Public Works', 'Safety Department'];
  const jurisdictionLevels = ['Municipal', 'Regional', 'National'];
  return <div className="min-h-screen bg-gray-50" data-id="element-2886">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2887">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-2888">
          <a href="/" className="hover:text-gray-700" data-id="element-2889">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-2890" />
          <a href="/admin" className="hover:text-gray-700" data-id="element-2891">
            Administration
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-2892" />
          <span className="text-gray-900" data-id="element-2893">Service Configuration</span>
        </div>
        {/* Header with Stats */}
        <div className="mb-8" data-id="element-2894">
          <div className="flex justify-between items-start mb-4" data-id="element-2895">
            <div data-id="element-2896">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-2897">
                Service Configuration
              </h1>
              <p className="mt-1 text-sm text-gray-500" data-id="element-2898">
                Configure and manage municipal services across all departments
              </p>
            </div>
            <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center" onClick={() => navigate('/admin/services/create')} data-id="element-2899">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-2900" />
              Add New Service
            </button>
          </div>
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4" data-id="element-2901">
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-2902">
              <div className="text-sm text-gray-500" data-id="element-2903">Total Services</div>
              <div className="text-2xl font-semibold text-gray-900" data-id="element-2904">
                {services.length}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-2905">
              <div className="text-sm text-gray-500" data-id="element-2906">Active Services</div>
              <div className="text-2xl font-semibold text-green-600" data-id="element-2907">
                {services.filter(s => s.status === 'active').length}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-2908">
              <div className="text-sm text-gray-500" data-id="element-2909">Service Categories</div>
              <div className="text-2xl font-semibold text-blue-600" data-id="element-2910">
                {categories.length}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-2911">
              <div className="text-sm text-gray-500" data-id="element-2912">Agencies</div>
              <div className="text-2xl font-semibold text-purple-600" data-id="element-2913">
                {agencies.length}
              </div>
            </div>
          </div>
        </div>
        {/* Filters and Actions */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6" data-id="element-2914">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4" data-id="element-2915">
            {/* Search */}
            <div className="flex-1" data-id="element-2916">
              <div className="relative" data-id="element-2917">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2918">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2919" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Search services by code, name, or description..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2920" />
              </div>
            </div>
            {/* Primary Filters */}
            <div className="mt-4 md:mt-0 flex flex-wrap gap-4" data-id="element-2921">
              <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-2922">
                <option value="all" data-id="element-2923">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat} data-id="element-2924">
                    {cat}
                  </option>)}
              </select>
              <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} data-id="element-2925">
                <option value="all" data-id="element-2926">All Statuses</option>
                <option value="active" data-id="element-2927">Active</option>
                <option value="inactive" data-id="element-2928">Inactive</option>
              </select>
              <select className="block w-full md:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedAgency} onChange={e => setSelectedAgency(e.target.value)} data-id="element-2929">
                <option value="all" data-id="element-2930">All Agencies</option>
                {agencies.map(agency => <option key={agency} value={agency} data-id="element-2931">
                    {agency}
                  </option>)}
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} data-id="element-2932">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-2933" />
                Advanced Filters
              </button>
            </div>
          </div>
          {/* Advanced Filters */}
          {showAdvancedFilters && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2934">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2935">
                <div data-id="element-2936">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2937">
                    Jurisdiction Level
                  </label>
                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-2938">
                    <option value="" data-id="element-2939">All Levels</option>
                    {jurisdictionLevels.map(level => <option key={level} value={level} data-id="element-2940">
                        {level}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-2941">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2942">
                    Fee Range
                  </label>
                  <div className="flex space-x-2" data-id="element-2943">
                    <input type="number" placeholder="Min" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-2944" />
                    <input type="number" placeholder="Max" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-2945" />
                  </div>
                </div>
                <div data-id="element-2946">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2947">
                    Last Updated
                  </label>
                  <div className="flex space-x-2" data-id="element-2948">
                    <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-2949" />
                    <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" data-id="element-2950" />
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-2" data-id="element-2951">
                <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2952">
                  Reset Filters
                </button>
                <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-2953">
                  Apply Filters
                </button>
              </div>
            </div>}
        </div>
        {/* Bulk Actions and Export */}
        <div className="flex justify-between items-center mb-4" data-id="element-2954">
          <div className="flex items-center space-x-2" data-id="element-2955">
            <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50" disabled={selectedServices.length === 0} data-id="element-2956">
              Bulk Enable
            </button>
            <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50" disabled={selectedServices.length === 0} data-id="element-2957">
              Bulk Disable
            </button>
          </div>
          <div className="flex items-center space-x-2" data-id="element-2958">
            <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2959">
              <DownloadIcon className="h-4 w-4" data-id="element-2960" />
            </button>
            <select className="block w-auto px-3 py-2 border border-gray-300 rounded-md text-sm" value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} data-id="element-2961">
              <option value={10} data-id="element-2962">10 per page</option>
              <option value={25} data-id="element-2963">25 per page</option>
              <option value={50} data-id="element-2964">50 per page</option>
              <option value={100} data-id="element-2965">100 per page</option>
            </select>
          </div>
        </div>
        {/* Service Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2966">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2967">
            <thead className="bg-gray-50" data-id="element-2968">
              <tr data-id="element-2969">
                <th scope="col" className="w-12 px-6 py-3" data-id="element-2970">
                  <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" onChange={e => {
                  if (e.target.checked) {
                    setSelectedServices(services.map(s => s.id));
                  } else {
                    setSelectedServices([]);
                  }
                }} data-id="element-2971" />
                </th>
                {[{
                field: 'serviceCode',
                label: 'Service Code'
              }, {
                field: 'serviceName',
                label: 'Service Name'
              }, {
                field: 'category',
                label: 'Category'
              }, {
                field: 'agency',
                label: 'Agency'
              }, {
                field: 'status',
                label: 'Status'
              }, {
                field: 'actions',
                label: 'Actions'
              }].map(column => <th key={column.field} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2972">
                    <button className="group inline-flex items-center space-x-1" onClick={() => {
                  if (column.field === sortField) {
                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortField(column.field);
                    setSortDirection('asc');
                  }
                }} data-id="element-2973">
                      <span data-id="element-2974">{column.label}</span>
                      <ArrowUpDownIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-500" data-id="element-2975" />
                    </button>
                  </th>)}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2976">
              {services.map(service => <tr key={service.id} className="hover:bg-gray-50" data-id="element-2977">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2978">
                    <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selectedServices.includes(service.id)} onChange={e => {
                  if (e.target.checked) {
                    setSelectedServices([...selectedServices, service.id]);
                  } else {
                    setSelectedServices(selectedServices.filter(id => id !== service.id));
                  }
                }} data-id="element-2979" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2980">
                    {service.serviceCode}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2981">
                    {service.serviceName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2982">
                    {service.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2983">
                    {service.agency}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-2984">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${service.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-2985">
                      {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-2986">
                    <div className="flex items-center space-x-2" data-id="element-2987">
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-2988">
                        <EyeIcon className="h-4 w-4" data-id="element-2989" />
                      </button>
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-2990">
                        <PencilIcon className="h-4 w-4" data-id="element-2991" />
                      </button>
                      <button className="text-gray-400 hover:text-gray-500" data-id="element-2992">
                        {service.status === 'active' ? <XIcon className="h-4 w-4" data-id="element-2993" /> : <CheckIcon className="h-4 w-4" data-id="element-2994" />}
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" data-id="element-2995">
            <div className="flex-1 flex justify-between sm:hidden" data-id="element-2996">
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2997">
                Previous
              </button>
              <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-2998">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-2999">
              <div data-id="element-3000">
                <p className="text-sm text-gray-700" data-id="element-3001">
                  Showing <span className="font-medium" data-id="element-3002">1</span> to{' '}
                  <span className="font-medium" data-id="element-3003">10</span> of{' '}
                  <span className="font-medium" data-id="element-3004">{services.length}</span> results
                </p>
              </div>
              <div data-id="element-3005">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-3006">
                  <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-3007">
                    Previous
                  </button>
                  <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-3008">
                    1
                  </button>
                  <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-3009">
                    2
                  </button>
                  <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-3010">
                    3
                  </button>
                  <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-3011">
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default ServiceConfigurationPage;